import * as React from "react";
import * as ReactDOM from "react-dom";
import Freeze from "@vingle/wtf-sdk/helpers/freeze";

// components
import { IconWrapper } from "../../icons";
import PACKS from "../../icons/packs";
import InfiniteScroller from "@vingle/wtf-sdk/components/infiniteScroller";
import * as EnvChecker from "@vingle/wtf-sdk/helpers/envChecker";

const styles = require("./fullscreenDialog.scss");

export enum DIALOG_TYPES {
  FOLLOWEE = "user_list.title_followee",
  FOLLOWER = "user_list.title_follower",
  BOOSTER = "user_list.title_booster",
  LIKED_USER = "user_list.like_count",
}

interface IInfiniteScroll {
  loader: React.ReactNode;
  loadMore: () => void;
  paging?: Vingle.IPaging;
  itemLength: number;
  threshold?: number;
}

interface IProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  infiniteOptions?: IInfiniteScroll;
}

class FullscreenDialog extends React.PureComponent<IProps> {
  private readonly el: HTMLDivElement | null = EnvChecker.isBrowser()
    ? document.createElement("div")
    : null;
  private readonly portalRoot: HTMLElement | null = EnvChecker.isBrowser()
    ? document.getElementById("vingle-portal")
    : null;
  private readonly freezer: Freeze = new Freeze();

  public componentDidMount() {
    if (this.portalRoot && this.el) this.portalRoot.appendChild(this.el);
    this.toggleScroll();
  }

  public componentDidUpdate() {
    this.toggleScroll();
  }

  public componentWillUnmount() {
    if (this.portalRoot && this.el) this.portalRoot.removeChild(this.el);
    this.freezer.off();
  }

  public render() {
    const { open } = this.props;
    if (!open) {
      return null;
    }

    const content = (
      <div
        className={styles.dialog}
        onKeyDown={this.handleKeyDown}
        ref={ele => {
          if (ele) ele.focus();
        }}
        tabIndex={0}
      >
        {this.renderFullScreenDialogChild()}
      </div>
    );

    if (this.el) {
      return ReactDOM.createPortal(content, this.el);
    }
    return content;
  }

  private readonly renderFullScreenDialogChild = () => {
    const { onClose, title, children, infiniteOptions } = this.props;
    const content = [
      <div
        key="portal-fullscreen-closeWrapper"
        className={styles.closeWrapper}
        onClick={onClose}
      >
        <IconWrapper
          icon={PACKS.USER_LIST.CLOSE}
          className={styles.closeButton}
        />
      </div>,
      <div
        key="portal-fullscreen-contentWrapper"
        className={styles.contentWrapper}
      >
        <div className={styles.header}>{title}</div>
        <div>{children}</div>
      </div>,
    ];

    if (!infiniteOptions) {
      return <div>{content}</div>;
    }
    return (
      <InfiniteScroller
        itemLength={infiniteOptions.itemLength}
        paging={infiniteOptions.paging}
        loadMore={infiniteOptions.loadMore}
        loader={
          <div className={styles.loader} key="fullscreen_loader">
            {infiniteOptions.loader}
          </div>
        }
        threshold={!infiniteOptions.threshold ? 250 : infiniteOptions.threshold}
      >
        {content}
      </InfiniteScroller>
    );
  };

  private readonly toggleScroll = () => {
    const { open } = this.props;
    if (open) {
      this.freezer.on();
    } else {
      this.freezer.off();
    }
  };

  private readonly handleKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
  ) => {
    if (event.keyCode === 27) {
      this.props.onClose();
    }
  };
}

export default FullscreenDialog;
